import { useEffect, useState, type FormEvent } from "react";
import type { AccommodationNode, TripDay } from "../types";
import { Dialog } from "./Dialog";

interface MapPointDialogProps {
  coordinates: { lat: number; lon: number } | null;
  onClose: () => void;
  onSave: (name: string) => void;
}

export function MapPointDialog({ coordinates, onClose, onSave }: MapPointDialogProps) {
  const [name, setName] = useState("Map point");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!coordinates) return;
    setName("Map point");
    setError("");
  }, [coordinates]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) {
      setError("Give this map point a name.");
      return;
    }
    onSave(cleanName);
    onClose();
  }

  return (
    <Dialog
      open={Boolean(coordinates)}
      title="Add map point"
      description="Name the coordinates you selected before adding them to this day."
      onClose={onClose}
      className="dialog-small"
    >
      <form className="dialog-form" onSubmit={submit}>
        <div className="field-grid">
          <label className="field field-span"><span>Place name</span><input autoFocus required value={name} onChange={(event) => setName(event.target.value)} /></label>
          <label className="field field-span"><span>Coordinates</span><input readOnly value={coordinates ? `${coordinates.lat.toFixed(5)}, ${coordinates.lon.toFixed(5)}` : ""} /></label>
        </div>
        <p className="form-error" role="alert">{error}</p>
        <div className="dialog-actions"><button className="button button-secondary" type="button" onClick={onClose}>Cancel</button><button className="button button-primary" type="submit">Add point</button></div>
      </form>
    </Dialog>
  );
}

interface FreeTimeDialogProps {
  dayLabel: string | null;
  onClose: () => void;
  onSave: (input: { durationMinutes: number; startTime?: string; notes?: string }) => void;
}

export function FreeTimeDialog({ dayLabel, onClose, onSave }: FreeTimeDialogProps) {
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [startTime, setStartTime] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!dayLabel) return;
    setDurationMinutes(60);
    setStartTime("");
    setNotes("");
    setError("");
  }, [dayLabel]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!Number.isInteger(durationMinutes) || durationMinutes < 15 || durationMinutes > 720) {
      setError("Free time must be between 15 and 720 minutes.");
      return;
    }
    onSave({ durationMinutes, ...(startTime ? { startTime } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}) });
    onClose();
  }

  return (
    <Dialog open={Boolean(dayLabel)} title="Keep free time" description={dayLabel ? `Reserve an open block on ${dayLabel}.` : undefined} onClose={onClose} className="dialog-small">
      <form className="dialog-form" onSubmit={submit}>
        <div className="field-grid">
          <label className="field"><span>Minutes</span><input autoFocus required type="number" min="15" max="720" step="15" value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))} /></label>
          <label className="field"><span>Starts <small>optional</small></span><input type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} /></label>
          <label className="field field-span"><span>Note <small>optional</small></span><textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Lunch, wandering, or recovery time" /></label>
        </div>
        <p className="form-error" role="alert">{error}</p>
        <div className="dialog-actions"><button className="button button-secondary" type="button" onClick={onClose}>Cancel</button><button className="button button-primary" type="submit">Keep time open</button></div>
      </form>
    </Dialog>
  );
}

interface AccommodationDialogProps {
  day: TripDay | null;
  label: string;
  onClose: () => void;
  onSave: (accommodation: AccommodationNode) => void;
  onRemove: () => void;
}

export function AccommodationDialog({ day, label, onClose, onSave, onRemove }: AccommodationDialogProps) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!day) return;
    setName(day.accommodation?.name ?? "");
    setAddress(day.accommodation?.address ?? "");
    setLat(day.accommodation?.lat === undefined ? "" : String(day.accommodation.lat));
    setLon(day.accommodation?.lon === undefined ? "" : String(day.accommodation.lon));
    setError("");
  }, [day]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!day) return;
    const cleanName = name.trim();
    if (!cleanName) {
      setError("Accommodation name is required.");
      return;
    }
    const hasLat = lat.trim() !== "";
    const hasLon = lon.trim() !== "";
    if (hasLat !== hasLon) {
      setError("Provide both latitude and longitude, or leave both blank.");
      return;
    }
    const latitude = hasLat ? Number(lat) : undefined;
    const longitude = hasLon ? Number(lon) : undefined;
    if ((latitude !== undefined && (!Number.isFinite(latitude) || latitude < -90 || latitude > 90)) || (longitude !== undefined && (!Number.isFinite(longitude) || longitude < -180 || longitude > 180))) {
      setError("Coordinates must be valid latitude and longitude values.");
      return;
    }
    onSave({
      id: day.accommodation?.id ?? `accommodation_${day.id}`,
      name: cleanName,
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(latitude !== undefined && longitude !== undefined ? { lat: latitude, lon: longitude } : {}),
    });
    onClose();
  }

  return (
    <Dialog open={Boolean(day)} title={day?.accommodation ? "Edit accommodation" : "Add accommodation"} description={`Use this as the start or end anchor for ${label}.`} onClose={onClose}>
      <form className="dialog-form" onSubmit={submit}>
        <fieldset className="dialog-section"><legend>Stay details</legend><div className="field-grid">
          <label className="field field-span"><span>Name</span><input autoFocus required value={name} onChange={(event) => setName(event.target.value)} placeholder="Hotel, apartment, or host" /></label>
          <label className="field field-span"><span>Address <small>optional</small></span><input value={address} onChange={(event) => setAddress(event.target.value)} /></label>
        </div></fieldset>
        <fieldset className="dialog-section"><legend>Route anchor <small>optional</small></legend><div className="field-grid">
          <label className="field"><span>Latitude</span><input inputMode="decimal" value={lat} onChange={(event) => setLat(event.target.value)} placeholder="35.0116" /></label>
          <label className="field"><span>Longitude</span><input inputMode="decimal" value={lon} onChange={(event) => setLon(event.target.value)} placeholder="135.7681" /></label>
        </div></fieldset>
        <p className="form-error" role="alert">{error}</p>
        <div className="dialog-actions">
          {day?.accommodation ? <button className="button button-danger" type="button" onClick={() => { onRemove(); onClose(); }}>Remove stay</button> : null}
          <button className="button button-secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="button button-primary" type="submit">Save stay</button>
        </div>
      </form>
    </Dialog>
  );
}
